// REST-поллер деградированного приёма (эпизод «WS душится провайдером», client-diag 2026-07-05).
//
// Наблюдение с устройства пользователя: WS-хендшейки к edge стабильно дропаются DPI-фильтром
// провайдера, при этом обычные HTTP-запросы к тому же серверу ходят за миллисекунды. Пока WS не
// в `ready`, этот поллер раз в intervalMs добирает пропущенное ЧЕРЕЗ REST и эмитит КАНОНИЧЕСКИЕ
// ServerEvent (sync.delta / unread.update / chat.updated) — стор применяет их тем же кодом, что
// и WS-кадры (никакой новой семантики, wire-форма из shared-contracts §4.7). WS поднялся → поллер
// автоматически замолкает (условие в тике), штатный sync доберёт остальное.
//
// Границы деградации (документированные):
//   • пропуск больше maxPagesPerChat×страницу — добирается только непрерывный хвост от последнего
//     сообщения; дыра в середине истории останется до loadOlder при скролле;
//   • новые чаты приходят как chat.updated (полный Chat из GET /chats) — как upsert стора;
//   • эфемерное (typing/presence) не поллится — только durable-состояние.
export function createRestPoller(deps) {
    const intervalMs = deps.intervalMs ?? 15_000;
    const maxPages = deps.maxPagesPerChat ?? 3;
    const pageLimit = deps.pageLimit ?? 50;
    const setIntervalFn = deps.setIntervalFn ?? ((fn, ms) => setInterval(fn, ms));
    const clearIntervalFn = deps.clearIntervalFn ?? ((h) => clearInterval(h));
    let timer = null;
    let polling = false;
    /** Последние отданные unreadCount по чатам — эмитим unread.update только на изменении. */
    const lastUnread = new Map();
    /** Непрерывный хвост сообщений (afterSeq, lastSeq] — страницами от конца, ASC. */
    async function fetchTail(chatId, afterSeq) {
        const acc = [];
        let before = undefined;
        for (let page = 0; page < maxPages; page++) {
            const batch = await deps.rest.history(chatId, {
                ...(before !== undefined ? { beforeSeq: before } : {}),
                limit: pageLimit,
            });
            if (batch.length === 0)
                break;
            acc.unshift(...batch); // страницы приходят ASC; более старая страница встаёт спереди
            const minSeq = batch[0].seq;
            if (minSeq <= afterSeq + 1)
                break; // докопались до курсора — хвост непрерывен
            before = minSeq;
        }
        return acc.filter((m) => m.seq > afterSeq);
    }
    async function pollNow() {
        if (polling)
            return;
        if (!deps.network.isOnline() || deps.isWsReady() || deps.isAuthBlocked())
            return;
        if (deps.isVisible !== undefined && !deps.isVisible())
            return;
        polling = true;
        try {
            const chats = await deps.rest.listChats();
            const cursors = new Map(deps.getCursors().map((c) => [c.chatId, c.afterSeq]));
            const total = chats.reduce((n, c) => n + (c.myState?.unreadCount ?? 0), 0);
            for (const chat of chats) {
                // WS мог подняться посреди тика — дальше он сам доберёт, не соревнуемся.
                if (deps.isWsReady())
                    return;
                const after = cursors.get(chat.id);
                const lastSeq = chat.lastMessage?.seq;
                if (after === undefined) {
                    // Чат, которого стор ещё не видел (создан без нас; chat.created по WS не существует) —
                    // отдаём полный Chat каноническим upsert-событием.
                    deps.emit({ t: 'chat.updated', chat });
                }
                else if (lastSeq !== undefined && lastSeq > after) {
                    const messages = await fetchTail(chat.id, after);
                    if (messages.length > 0) {
                        deps.emit({
                            t: 'sync.delta',
                            chatId: chat.id,
                            messages,
                            upToSeq: messages[messages.length - 1].seq,
                        });
                    }
                }
                const unread = chat.myState?.unreadCount ?? 0;
                if (lastUnread.get(chat.id) !== unread) {
                    lastUnread.set(chat.id, unread);
                    deps.emit({ t: 'unread.update', chatId: chat.id, unreadCount: unread, total });
                }
            }
            deps.onTick?.();
        }
        catch {
            // сетевой сбой тика — молча ждём следующий (поллер не для идеальной сети и существует)
        }
        finally {
            polling = false;
        }
    }
    return {
        start() {
            if (timer !== null)
                return;
            timer = setIntervalFn(() => void pollNow(), intervalMs);
        },
        stop() {
            if (timer !== null) {
                clearIntervalFn(timer);
                timer = null;
            }
        },
        pollNow,
    };
}
//# sourceMappingURL=rest-poller.js.map